import { StrictMode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import { isEditorLoggedIn, preload, updateContent } from "scrivito"
import { configureScrivitoEditing } from "scrivito/editing"
import { ensureSiteIsPresent } from "scrivito/editing/scrivitoSites"
import { initialLocale } from "shared/lib/locale/contentLanguage"

import Honeybadger from "@honeybadger-io/js"
import I18n from "i18n"

import { App } from "./app"
import "./scrivito/Data"
import "./scrivito/Objs"
import "./scrivito/Widgets"

Honeybadger.beforeNotify(notice => {
  if (notice?.message?.includes("ResizeObserver loop")) {
    return false
  }
  // Hydration mismatch on product pages: base HTML is for the default variant,
  // but configurationKey in the URL causes Scrivito to render a different variant
  // on the client. The entire stack is inside Scrivito's bundle — not actionable.
  if (
    notice?.message?.match(/Minified React error #(418|423|425)/) &&
    notice?.url?.includes("/product/")
  ) {
    return false
  }
  // Stale chunk after deployment: old bundle references chunk hashes that no longer
  // exist. ErrorBoundary auto-reloads to recover — nothing to act on here.
  const msg = notice?.message ?? ""
  if (
    msg.includes("Unable to preload") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed")
  ) {
    return false
  }
  // Triggered by bots probing open-redirect paths with headless Chrome.
  // A browser extension monkey-patches appendChild and tries to JSON.stringify
  // a React-annotated SVGUseElement, hitting React's circular fiber reference.
  if (
    msg.includes("Converting circular structure to JSON") &&
    msg.includes("SVGUseElement") &&
    msg.includes("__reactFiber$")
  ) {
    return false
  }
  // Cancelled in-flight requests (navigation, refetch, unmount) surface as unhandled
  // AbortErrors without a backtrace. Timeouts are reported as TimeoutError instead.
  if (msg.includes("UnhandledPromiseRejectionWarning: AbortError")) {
    return false
  }
  // Safari < 17 lacks URL.canParse; the call is inside Scrivito's bundle — not actionable.
  if (msg.includes("URL.canParse is not a function")) {
    return false
  }
  return true
})

configureScrivitoEditing()
ensureSiteIsPresent()

declare global {
  interface Window {
    preloadDump?: unknown
    Cookiebot?: {
      renew: () => void
    }
  }
}

function renderApp() {
  createRoot(document.getElementById("application") as HTMLElement).render(
    <StrictMode>
      <App />
    </StrictMode>
  )
}

// Minified hydration errors carry no detail. The <html> class and lang reveal browser
// translation (translated-ltr/rtl), which rewrites text nodes before React hydrates.
function reportRecoverableError(
  error: unknown,
  errorInfo: { componentStack?: string }
) {
  Honeybadger.notify(error instanceof Error ? error : String(error), {
    context: {
      htmlClass: document.documentElement.className,
      htmlLang: document.documentElement.lang,
      componentStack: errorInfo.componentStack
    }
  })
}

function hydrateApp() {
  hydrateRoot(
    document.getElementById("application") as HTMLElement,
    <StrictMode>
      <App
        appWrapperRef={el => {
          if (el) updateContent()
        }}
      />
    </StrictMode>,
    { onRecoverableError: reportRecoverableError }
  )
}

// i18next boots at the default locale, so a prerendered page in any other locale
// would be repainted in the wrong language until LocaleVerifier's effect pushes
// the URL locale in — and React would discard the SSR markup over the resulting
// text mismatch. Resources are bundled, so this resolves synchronously.
I18n.changeLanguage(initialLocale())

if (typeof window.preloadDump === "string") {
  preload(window.preloadDump).then(({ dumpLoaded }) => {
    delete window.preloadDump

    if (dumpLoaded) {
      hydrateApp()
    } else {
      renderApp()
    }
  })
} else renderApp()

if (isEditorLoggedIn()) {
  import("./scrivito/Objs/editingConfig")
  import("./scrivito/Widgets/editingConfig")
  import("./scrivito/Data/editingConfig")
}
